import React, { useEffect, useMemo } from "react";
import { Button, Grid, TextField } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";

import AppForm from "components/Shared/AppForm";
import AppSwitch from "components/Shared/AppSwitch";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useAuth } from "hooks";
import { getDateTimeAuto } from "utils/dateFormats";
import {
    useLazyGetDdlDriverUnloadingCombinedQuery,
} from "store/services/shipmentLoadingApi";
import { useLazyGetDDLBranchListQuery } from "store/services/branchApi";

const INITIAL_STATE = {
    account: null,
    entity: null,
    selectBranch: [],
    loadingId: [],
    consolidationId: null,
    collectionId: null,
    shipmentId: [],
    unloadingTime: getDateTimeAuto(),
    unloadingLocation: { lat: null, lon: null },
    isActive: true,
    unloadingBranch: null,
};

const DriverShipmentUnLoadingForm = ({
    isBusy = null,
    shipmentUnLoadingDetail = null,
    onSubmit = () => { },
}) => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const { viewEntity, isAccountAdmin, isSystemAdmin, loggedInUser } = useAuth();

    const {
        control,
        handleSubmit,
        setValue,
        formState: { errors },
        reset,
    } = useForm({
        defaultValues: {
            ...INITIAL_STATE,
            entity: viewEntity?.entity?._id ? viewEntity.entity : null,
            account: viewEntity?.entity?.account?._id ? viewEntity.entity.account : null,
            selectBranch: state?.unloadingBranch ? [state.unloadingBranch] : [],
            loadingId: state?.loading || null,
            shipmentId: state?.shipments || [],
            unloadingBranch: null,
        },
    });

    const _watchEntity = useWatch({ control, name: "entity" });
    const _watchAccount = useWatch({ control, name: "account" });
    const _watchSelectBranch = useWatch({ control, name: "selectBranch" });
    const _watchLoadingId = useWatch({ control, name: "loadingId" });
    const _watchShipmentId = useWatch({ control, name: "shipmentId" });

    const { data: accounts, isLoading: loadingAccounts } = useGetDDLAccountListQuery();
    const [fetchEntities, { data: entities, isLoading: loadingEntities }] = useLazyGetDDLEntityListQuery();

    // Combined query: branches + loadings + shipments in one call
    const [
        fetchCombinedData,
        {
            data: combinedData,
            isLoading: loadingCombined,
            isFetching: fetchingCombined,
        }
    ] = useLazyGetDdlDriverUnloadingCombinedQuery();

    const [
        fetchBranchList,
        { data: branchListData, isLoading: loadingBranchList },
    ] = useLazyGetDDLBranchListQuery();

    useEffect(() => {
        const accountId = _watchAccount?._id || viewEntity?.entity?.account?._id;
        const entityId = _watchEntity?._id || viewEntity?.entity?._id;
        if (accountId && entityId) {
            fetchBranchList({ account: accountId, entity: entityId });
        }
    }, [_watchAccount?._id, _watchEntity?._id, viewEntity, fetchBranchList]);

    // Available branches from combined API
    const availableSelectBranches = useMemo(() => {
        return combinedData?.data || [];
    }, [combinedData]);

    // Derive loadings from selected branches (no second API call needed)
    const LoadedShipments = useMemo(() => {
        const selected = _watchSelectBranch || [];
        if (!selected.length || !combinedData?.data) return [];

        // A single loading can contain shipments for multiple selected
        // branches. Merge those branch-specific slices so the same loading
        // is presented once and all of its eligible shipments remain usable.
        const loadingsById = new Map();
        selected.forEach((selectedBranch) => {
            const branchData = combinedData.data.find(
                (b) => b._id === selectedBranch._id && b.branchType === selectedBranch.branchType
            );
            if (branchData?.loadings) {
                branchData.loadings.forEach((loading) => {
                    const existing = loadingsById.get(loading._id);
                    if (!existing) {
                        loadingsById.set(loading._id, {
                            ...loading,
                            shipment: [...(loading.shipment || [])],
                        });
                        return;
                    }

                    const shipmentIds = new Set(
                        (existing.shipment || []).map((shipment) => shipment._id),
                    );
                    (loading.shipment || []).forEach((shipment) => {
                        if (!shipmentIds.has(shipment._id)) {
                            existing.shipment.push(shipment);
                            shipmentIds.add(shipment._id);
                        }
                    });
                });
            }
        });
        return [...loadingsById.values()];
    }, [_watchSelectBranch, combinedData]);

    // Fetch combined data once when driver is available
    useEffect(() => {
        if (loggedInUser?.typeRef) {
            fetchCombinedData({ driverId: loggedInUser.typeRef });
        }
    }, [loggedInUser?.typeRef, fetchCombinedData]);

    // Get available shipments for selected loading(s) and branch
    const availableShipments = useMemo(() => {
        const loadingIds = Array.isArray(_watchLoadingId) ? _watchLoadingId : (_watchLoadingId ? [_watchLoadingId] : []);

        if (loadingIds.length > 0) {
            let allShipments = [];

            loadingIds.forEach(loadingId => {
                const loading = LoadedShipments.find(l => l._id === loadingId._id);
                if (loading) {
                    let shipments = loading.shipment?.filter(
                        (shipment) =>
                            (shipment.status === "Loaded" || shipment.status === "Return") &&
                            shipment.isOnHold !== true
                    ) || [];

                    allShipments = [...allShipments, ...shipments];
                }
            });

            return allShipments;
        }

        if (state?.shipments?.length) {
            return state.shipments;
        }

        return [];
    }, [_watchLoadingId, LoadedShipments, state?.shipments]);

    // Auto-populate loadingId from state
    useEffect(() => {
        if (state?.loading && LoadedShipments?.length) {
            const matchingLoading = LoadedShipments.find(
                (load) => load._id === state.loading._id
            );
            setValue("loadingId", matchingLoading || state.loading);
        }
    }, [state, LoadedShipments, setValue]);

    // Auto-populate shipmentId when loadingId changes
    useEffect(() => {
        const loadingIds = Array.isArray(_watchLoadingId) ? _watchLoadingId : (_watchLoadingId ? [_watchLoadingId] : []);

        if (loadingIds.length > 0 && !state?.shipments) {
            let allShipmentsToSelect = [];

            loadingIds.forEach(loadingId => {
                const loading = LoadedShipments.find(l => l._id === loadingId._id);
                if (loading) {
                    let shipmentsToSelect = loading.shipment?.filter(
                        (shipment) =>
                            (shipment.status === "Loaded" || shipment.status === "Return") &&
                            shipment.isOnHold !== true
                    ) || [];

                    allShipmentsToSelect = [...allShipmentsToSelect, ...shipmentsToSelect];
                }
            });

            const loadedShipmentIds = allShipmentsToSelect.map((shipment) => ({
                _id: shipment._id,
                shipmentId: shipment.shipmentId,
                isReturning: shipment.IsReturning || false,
                status: shipment.status,
            }));

            setValue("shipmentId", loadedShipmentIds, { shouldValidate: true });
        } else if (loadingIds.length === 0 && !state?.shipments) {
            setValue("shipmentId", [], { shouldValidate: true });
        }
    }, [_watchLoadingId, LoadedShipments, state, setValue]);

    useEffect(() => {
        if (isAccountAdmin || isSystemAdmin) {
            fetchEntities({
                account: _watchAccount?._id ?? viewEntity?.entity?.account?._id,
            });
        }
    }, [
        _watchAccount?._id,
        fetchEntities,
        isAccountAdmin,
        isSystemAdmin,
        viewEntity?.entity?.account,
    ]);

    useEffect(() => {
        if (!shipmentUnLoadingDetail) return;
        const unloadingBranch = shipmentUnLoadingDetail?.unLoadedBranch ||
            shipmentUnLoadingDetail?.transitBranch ||
            null;
        reset({
            ...INITIAL_STATE,
            ...shipmentUnLoadingDetail,
            unloadingTime: shipmentUnLoadingDetail?.unloadingTime
                ? new Date(shipmentUnLoadingDetail.unloadingTime).toISOString().slice(0, 16)
                : getDateTimeAuto(),
            consolidationId: shipmentUnLoadingDetail?.consolidationId || null,
            collectionId: shipmentUnLoadingDetail?.collectionId || null,
            unloadingBranch: unloadingBranch,
            shipmentId: shipmentUnLoadingDetail?.shipmentId?.map((item) => ({
                _id: item.shipment?._id,
                shipmentId: item.shipment?.shipmentId,
                isReturning: item.shipment?.IsReturning || false,
                status: item.shipment?.status,
            })) || [],
            account: shipmentUnLoadingDetail?.account?._id
                ? shipmentUnLoadingDetail.account
                : null,
            entity: shipmentUnLoadingDetail?.entity?._id
                ? shipmentUnLoadingDetail.entity
                : null,
            loadingId: Array.isArray(shipmentUnLoadingDetail?.loadingId)
                ? shipmentUnLoadingDetail.loadingId
                : [],
            unloadingLocation: {
                lat: shipmentUnLoadingDetail?.unloadingLocation?.lat || null,
                lon: shipmentUnLoadingDetail?.unloadingLocation?.lon || null,
            },
        });
        const accountId = shipmentUnLoadingDetail?.account?._id ||
            viewEntity?.entity?.account?._id ||
            null;

        if (accountId && (isAccountAdmin || isSystemAdmin)) {
            fetchEntities({ account: accountId });
        }
    }, [
        reset,
        shipmentUnLoadingDetail,
        fetchEntities,
        isAccountAdmin,
        isSystemAdmin,
        viewEntity?.entity,
    ]);

    const availableUnloadingBranches = useMemo(() => branchListData || [], [branchListData]);

    const handleOnShipmentUnLoadingSubmit = async (data) => {
        const loadingIds = Array.isArray(data.loadingId)
            ? data.loadingId.map((l) => l._id)
            : data.loadingId
                ? [data.loadingId._id]
                : [];
        const payload = {
            entity: data?.entity?._id || null,
            account: data?.account?._id || null,
            loadingId: loadingIds,
            shipmentId: (data.shipmentId || []).map((s) => ({
                shipment: s._id,
                status: "Unloaded",
            })),
            unloadingBranch: data.unloadingBranch?._id || null,
            unloadingTime: data.unloadingTime,
            unloadingLocation: {
                lat: data.unloadingLocation.lat ? Number(data.unloadingLocation.lat) : null,
                lon: data.unloadingLocation.lon ? Number(data.unloadingLocation.lon) : null,
            },
            collectionId: data.collectionId || null,
            consolidationId: data.consolidationId || null,
        };
        await onSubmit(payload);
        if (!shipmentUnLoadingDetail) {
            reset({
                ...INITIAL_STATE,
                entity: viewEntity?.entity?._id ? viewEntity.entity : null,
                account: viewEntity?.entity?.account?._id ? viewEntity.entity.account : null,
            });
        }
    };

    return (
        <AppForm loading={isBusy}>
            <AppForm.Content>
                <Grid container spacing={4}>
                    {(isAccountAdmin || isSystemAdmin) && (
                        <Grid item md={6}>
                            <Controller
                                name="account"
                                control={control}
                                rules={{ required: "Please select an account." }}
                                render={({ field }) => (
                                    <BaseAutocomplete
                                        {...field}
                                        fullWidth
                                        label="Account"
                                        required
                                        value={field.value ?? null}
                                        onChange={(_, data) => {
                                            field.onChange(data);
                                            setValue("entity", null);
                                            setValue("selectBranch", []);
                                            setValue("loadingId", null);
                                            setValue("consolidationId", null);
                                            setValue("collectionId", null);
                                            setValue("shipmentId", []);
                                            setValue("unloadingLocation", { lat: null, lon: null });
                                            setValue("unloadingBranch", null);
                                        }}
                                        getOptionLabel={(option) => option?.name ?? ""}
                                        isOptionEqualToValue={(option, value) =>
                                            option?._id === value?._id || value === null || value === ""
                                        }
                                        options={accounts || []}
                                        loading={loadingAccounts}
                                        loadingText="Loading Account..."
                                        disabled={isAccountAdmin || loadingAccounts}
                                        helperText={errors?.account?.message}
                                        error={!!errors?.account}
                                    />
                                )}
                            />
                        </Grid>
                    )}
                    {(isAccountAdmin || isSystemAdmin) && (
                        <Grid item md={6}>
                            <Controller
                                name="entity"
                                control={control}
                                rules={{ required: "Please select an entity." }}
                                render={({ field: { value, onChange } }) => (
                                    <BaseAutocomplete
                                        fullWidth
                                        required
                                        label="Entity"
                                        value={value ?? null}
                                        onChange={(_, data) => {
                                            onChange(data);
                                            setValue("selectBranch", []);
                                            setValue("loadingId", null);
                                            setValue("consolidationId", null);
                                            setValue("collectionId", null);
                                            setValue("shipmentId", []);
                                            setValue("unloadingBranch", null);
                                            setValue("unloadingLocation", { lat: null, lon: null });
                                        }}
                                        getOptionLabel={(option) => option?.name ?? ""}
                                        isOptionEqualToValue={(option, value) =>
                                            option?._id === value?._id || value === null || value === ""
                                        }
                                        options={entities || []}
                                        loading={loadingEntities}
                                        loadingText="Loading Entities..."
                                        disabled={loadingEntities}
                                        helperText={errors?.entity?.message}
                                        error={!!errors?.entity}
                                    />
                                )}
                            />
                        </Grid>
                    )}

                    <Grid item xs={6}>
                        <Controller
                            name="selectBranch"
                            control={control}
                            rules={{ required: "Please select at least one branch." }}
                            render={({ field: { value, onChange } }) => (
                                <BaseAutocomplete
                                    fullWidth
                                    multiple
                                    required
                                    label="Select Branch"
                                    value={Array.isArray(value) ? value : []}
                                    onChange={(_, data) => {
                                        const previousIds = new Set(
                                            (Array.isArray(value) ? value : []).map((b) => b._id)
                                        );
                                        const newIds = new Set((data || []).map((b) => b._id));
                                        const branchWasRemoved = [...previousIds].some(
                                            (id) => !newIds.has(id)
                                        );
                                        onChange(data);
                                        if (branchWasRemoved) {
                                            setValue("loadingId", null);
                                            setValue("shipmentId", []);
                                        }
                                    }}
                                    getOptionLabel={(option) =>
                                        option
                                            ? `${option?.name} - ${option?.code} (${option?.branchType === "transit" ? "Transit" : "Destination"})`
                                            : ""
                                    }
                                    isOptionEqualToValue={(option, value) =>
                                        option?._id === value?._id && option?.branchType === value?.branchType
                                    }
                                    options={availableSelectBranches || []}
                                    loading={loadingCombined}
                                    loadingText="Loading branches..."
                                    helperText={errors?.selectBranch?.message}
                                    error={!!errors?.selectBranch}
                                    disabled={!!state?.unloadingBranch || loadingCombined}
                                    noOptionsText="No branches with loadings available"
                                />
                            )}
                        />
                    </Grid>

                    <Grid item md={6}>
                        <Controller
                            name="loadingId"
                            control={control}
                            rules={{ required: "Please select at least one loading." }}
                            render={({ field: { value, onChange } }) => (
                                <BaseAutocomplete
                                    fullWidth
                                    multiple
                                    label="Loading"
                                    required
                                    value={Array.isArray(value) ? value : (value ? [value] : [])}
                                    onChange={(_, data) => {
                                        onChange(data);
                                    }}
                                    getOptionLabel={(option) =>
                                        option?.loadingId || option?._id || ""
                                    }
                                    isOptionEqualToValue={(option, value) =>
                                        option?._id === value?._id
                                    }
                                    options={LoadedShipments}
                                    loading={loadingCombined || fetchingCombined}
                                    loadingText="Loading Shipments..."
                                    helperText={errors?.loadingId?.message}
                                    error={!!errors?.loadingId}
                                    disabled={
                                        !!state?.loading ||
                                        loadingCombined ||
                                        fetchingCombined ||
                                        !_watchSelectBranch?.length
                                    }
                                    noOptionsText={!_watchSelectBranch?.length ? "Please select a branch first" : "No loadings available"}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item md={6}>
                        <Controller
                            name="shipmentId"
                            control={control}
                            rules={{
                                required: {
                                    value: !!_watchSelectBranch?.length,
                                    message: "Please select at least one shipment."
                                },
                            }}
                            render={({ field: { value, onChange } }) => (
                                <BaseAutocomplete
                                    fullWidth
                                    multiple
                                    label="Shipment"
                                    required
                                    value={Array.isArray(value) ? value : []}
                                    onChange={(_, data) => {
                                        onChange(data);
                                    }}
                                    getOptionLabel={(option) =>
                                        option?.shipmentId
                                            ? option.isReturning || option.status === "Return"
                                                ? `${option.shipmentId} (R)`
                                                : option.shipmentId
                                            : option._id || ""
                                    }
                                    isOptionEqualToValue={(option, value) =>
                                        option?._id === value?._id
                                    }
                                    options={availableShipments}
                                    loading={loadingCombined || fetchingCombined}
                                    loadingText="Loading Shipments..."
                                    helperText={errors?.shipmentId?.message}
                                    error={!!errors?.shipmentId}
                                    disabled={
                                        !!state?.shipments ||
                                        loadingCombined ||
                                        fetchingCombined ||
                                        !_watchLoadingId
                                    }
                                    noOptionsText={!_watchLoadingId ? "Please select a loading first" : "No shipments available"}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <Controller
                            name="unloadingTime"
                            control={control}
                            defaultValue={INITIAL_STATE.unloadingTime}
                            rules={{ required: "Unloading Time is required" }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Unloading Time"
                                    type="datetime-local"
                                    fullWidth
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    error={!!errors.unloadingTime}
                                    helperText={errors?.unloadingTime?.message}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <Controller
                            name="unloadingBranch"
                            control={control}
                            rules={{ required: "Please select an unloading branch." }}
                            render={({ field: { value, onChange } }) => (
                                <BaseAutocomplete
                                    fullWidth
                                    required
                                    label="Unloading Branch"
                                    value={value || null}
                                    onChange={(_, data) => onChange(data)}
                                    getOptionLabel={(option) =>
                                        option ? `${option.name ?? ""} (${option.code ?? ""})` : ""
                                    }
                                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                                    options={availableUnloadingBranches}
                                    loading={loadingBranchList}
                                    loadingText="Loading branches..."
                                    helperText={errors?.unloadingBranch?.message}
                                    error={!!errors?.unloadingBranch}
                                    noOptionsText="No branches available"
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <Controller
                            name="unloadingLocation.lat"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    size="small"
                                    label="Unloading Latitude (optional)"
                                    type="number"
                                    value={field.value === null ? '' : field.value}
                                    onChange={(e) => {
                                        field.onChange(
                                            e.target.value === "" ? null : Number(e.target.value)
                                        );
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <Controller
                            name="unloadingLocation.lon"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    size="small"
                                    label="Unloading Longitude (optional)"
                                    type="number"
                                    value={field.value === null ? '' : field.value}
                                    onChange={(e) => {
                                        field.onChange(
                                            e.target.value === "" ? null : Number(e.target.value)
                                        );
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    {shipmentUnLoadingDetail && (
                        <Grid item xs={6}>
                            <Controller
                                name="isActive"
                                control={control}
                                render={({ field }) => (
                                    <AppSwitch {...field} label="Is Active?" />
                                )}
                            />
                        </Grid>
                    )}
                </Grid>
            </AppForm.Content>
            <AppForm.Actions>
                <Button onClick={() => navigate(-1)}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit(handleOnShipmentUnLoadingSubmit)}
                >
                    {shipmentUnLoadingDetail ? "Update" : "Add"}
                </Button>
            </AppForm.Actions>
        </AppForm>
    );
};

export default DriverShipmentUnLoadingForm;
